/**
 * Tender offers (F1, docs/cef_tender_build.md): issuer tender offers (SEC form SC TO-I) read by
 * Claude for an odd-lot provision, with the potential gain for 99 shares. Information only: the
 * list is read from the filing by a model and must be checked against the filing before acting.
 * Nothing here is a trade signal and nothing here places an order.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, Button, Icon, Panel, StatCell, Switch, etSyd, fmt, etAndLocal } from "@/sentinel";
import { QueryBoundary, UpdatedAt, retryTransient } from "@/lib/queryState";

const TYPE_LABEL = { fixed: "Fixed price", dutch_auction: "Dutch auction", exchange: "Exchange offer", other: "Other" };
const CONFIDENCE_LABEL = { high: "High", medium: "Medium", low: "Low" };
const isNum = (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v));
const usd = (v) => (isNum(v) ? fmt(Number(v), "price") : null);

/** Whole days from today (local) to an ISO date; negative once past. */
function daysLeft(iso) {
  if (!iso) return null;
  const end = new Date(`${iso}T23:59:59`);
  if (Number.isNaN(end.getTime())) return null;
  return Math.floor((end.getTime() - Date.now()) / 86400000);
}

function isRange(t) {
  return isNum(t?.price_low) && isNum(t?.price_high) && Number(t.price_low) !== Number(t.price_high);
}

function priceLabel(t) {
  if (!t) return "Not stated";
  const lo = usd(t.price_low);
  const hi = usd(t.price_high);
  const cur = t.currency && t.currency !== "USD" ? ` ${t.currency}` : "";
  if (lo && hi && isRange(t)) return `$${lo} to $${hi}${cur}`;
  if (lo || hi) return `$${lo || hi}${cur}`;
  return "Not stated";
}

function expiryLabel(t) {
  const d = t?.expiration_date;
  if (!d) return "Not stated";
  const n = daysLeft(d);
  if (n === null) return d;
  if (n < 0) return `${d} (passed)`;
  if (n === 0) return `${d} (today)`;
  return `${d} (${n} day${n === 1 ? "" : "s"} left)`;
}

function OddLotCell({ t }) {
  if (!t) return <span className="sn-muted">Unclear</span>;
  if (t.confidence === "low") return <span className="sn-muted" title="The model's confidence in this reading is low">Unclear</span>;
  if (t.odd_lot_provision) {
    return <Badge tone="brand" icon="Check">{`Yes${t.odd_lot_threshold ? ` (under ${t.odd_lot_threshold} shares)` : ""}`}</Badge>;
  }
  return <span>No</span>;
}

function gainLabel(o) {
  if (!isNum(o.odd_lot_gain_99)) return null;
  const g = Number(o.odd_lot_gain_99);
  return `${g < 0 ? "-" : "+"}$${Math.abs(g).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function isOddLot(o) {
  return o.status === "read" && !!o.terms?.odd_lot_provision && o.terms?.confidence !== "low";
}

/** Odd-lot offers first (largest gain first), then everything else newest filed first. */
function orderOffers(rows) {
  const odd = rows.filter(isOddLot).sort((a, b) => (Number(b.odd_lot_gain_99) || -1e9) - (Number(a.odd_lot_gain_99) || -1e9));
  const rest = rows.filter((o) => !isOddLot(o));
  return [...odd, ...rest];
}

function CompanyCell({ o }) {
  return (
    <div className="sn-stack" style={{ gap: 2 }}>
      <a href={o.index_url} target="_blank" rel="noreferrer" className="sn-link" data-testid={`tenders-link-${o.accession}`}>
        {o.company || o.terms?.issuer || o.accession}
      </a>
      <span className="sn-small sn-faint">
        {o.form}{o.form === "SC TO-I/A" ? " (amendment)" : ""} · filed {o.filed_at ? etSyd(o.filed_at, { seconds: false }) : "date unknown"}
      </span>
    </div>
  );
}

function RereadButton({ o, owner, reread }) {
  if (!owner) return null;
  return (
    <Button size="sm" variant="ghost" icon="RefreshCw" loading={reread.isPending && reread.variables === o.accession}
            onClick={() => reread.mutate(o.accession)} data-testid={`tenders-reread-${o.accession}`}>
      Read again
    </Button>
  );
}

function OfferRow({ o, owner, reread }) {
  const odd = isOddLot(o);
  const style = odd ? { background: "var(--brand-dim)" } : undefined;
  if (o.status === "unread" || o.status === "error") {
    const couldNot = o.status === "error";
    return (
      <tr key={o.accession} data-testid={`tenders-row-${o.accession}`} data-state={o.status}>
        <td style={{ whiteSpace: "normal" }}><CompanyCell o={o} /></td>
        <td colSpan={8} style={{ whiteSpace: "normal" }}>
          {couldNot ? (
            <span><Icon name="TriangleAlert" size={14} /> Could not read this filing after {o.attempts} tries{o.error ? `: ${o.error}` : ""}.</span>
          ) : (
            <span className="sn-muted">
              Not read yet{o.attempts ? ` (${o.attempts} of 3 tries so far${o.error ? `, last error: ${o.error}` : ""})` : ""}.
            </span>
          )}
        </td>
        <td><RereadButton o={o} owner={owner} reread={reread} /></td>
      </tr>
    );
  }
  const t = o.terms || {};
  const gain = gainLabel(o);
  return (
    <tr key={o.accession} data-testid={`tenders-row-${o.accession}`} data-state={odd ? "odd-lot" : o.status} style={style}>
      <td style={{ whiteSpace: "normal" }}><CompanyCell o={o} /></td>
      <td className="id">{o.ticker || <span className="sn-muted">None found</span>}</td>
      <td className="num">{priceLabel(t)}</td>
      <td>{TYPE_LABEL[t.offer_type] || "Other"}</td>
      <td className="num">{expiryLabel(t)}</td>
      <td><OddLotCell t={t} /></td>
      <td className="num">
        {isNum(o.market_price) ? `$${usd(o.market_price)}` : <span className="sn-muted">No price</span>}
        {o.price_date ? <span className="sn-small sn-faint"> {o.price_date}</span> : null}
      </td>
      <td className="num" style={{ color: "var(--text-primary)", fontWeight: odd ? 600 : undefined }}>
        {gain || <span className="sn-muted">Needs both prices</span>}
        {gain && isRange(t) ? <span className="sn-small sn-faint"> at the low end</span> : null}
      </td>
      <td>{CONFIDENCE_LABEL[t.confidence] || "Low"}</td>
      <td><RereadButton o={o} owner={owner} reread={reread} /></td>
    </tr>
  );
}

function OffersTable({ rows, owner, reread, label, testId }) {
  return (
    <div className="sn-table-wrap" data-testid={testId}>
      <table className="sn-table" aria-label={label}>
        <thead><tr>
          <th>Company (link to the filing)</th><th>Ticker</th><th className="r">Offer price</th><th>Type</th>
          <th className="r">Expires</th><th>Odd lot</th><th className="r">Market price</th>
          <th className="r">Gain for 99 shares</th><th>Confidence</th><th />
        </tr></thead>
        <tbody>
          {rows.map((o) => <OfferRow key={o.accession} o={o} owner={owner} reread={reread} />)}
        </tbody>
      </table>
    </div>
  );
}

function StatusLine({ data, at }) {
  const c = data?.counts || {};
  const b = data?.budget || {};
  const feed = data?.health?.feed;
  const r = data?.last_result;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="tenders-status">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCell size="sm" label="Open offers" value={String(c.open ?? 0)}
                  sub={`${c.odd_lot_open ?? 0} with an odd-lot provision`} />
        <StatCell size="sm" label="Not read yet" value={String(c.unread ?? 0)}
                  sub={c.error ? `${c.error} could not be read` : "all filings read"} />
        <StatCell size="sm" label="Expired" value={String(c.expired ?? 0)} sub="kept as a record" />
        <StatCell size="sm" label="Claude spend this month" value={isNum(b.spent_usd) ? fmt(b.spent_usd, "usd") : "$0"}
                  sub={`of a ${isNum(b.budget_usd) ? fmt(b.budget_usd, "usd") : "$10"} budget${b.blocked_calls ? ` · ${b.blocked_calls} calls blocked` : ""}`} />
      </div>
      <p className="sn-small sn-muted" style={{ margin: 0 }}>
        Watcher {data?.enabled ? "on" : "paused"}.{" "}
        {data?.last_poll_at ? `Last poll ${etSyd(data.last_poll_at)}` : "No poll has run yet"}
        {r ? ` (${r.new} new, ${r.read} read, ${r.errors} errors, ${r.expired} expired)` : ""}.
        {feed && feed.ok === false ? ` The SEC feed could not be fetched on the last poll: ${feed.error || "unknown error"}.` : ""}
        {" "}Polls run at {["07:30", "11:30", "15:30", "18:30"].map((t) => etAndLocal(t)).join(", ")} on weekdays. <UpdatedAt at={at} prefix="Shown" />
      </p>
    </div>
  );
}

export default function EquitiesTenders() {
  const { can } = useAuth();
  const owner = can("owner");
  const qc = useQueryClient();
  const [showExpired, setShowExpired] = useState(false);
  const q = useQuery({
    queryKey: ["eq-tenders", showExpired],
    queryFn: () => api.eqTenders(showExpired),
    refetchInterval: 5 * 60 * 1000,
    retry: retryTransient,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["eq-tenders"] });
  const onError = (e) => toast.error(fmtErr(e.response?.data?.detail) || e.message);
  const poll = useMutation({
    mutationFn: () => api.eqTendersPoll(),
    onSuccess: (r) => { toast.success(`Poll done: ${r.new} new, ${r.read} read, ${r.errors} errors, ${r.expired} expired.`); refresh(); },
    onError,
  });
  const reread = useMutation({
    mutationFn: (accession) => api.eqTenderReread(accession),
    onSuccess: (d) => {
      if (d?.status === "read" || d?.status === "expired") toast.success(`Read again: ${d.company || d.accession}.`);
      else toast.error(`Could not read ${d?.company || d?.accession}${d?.error ? `: ${d.error}` : ""}.`);
      refresh();
    },
    onError,
  });
  const toggle = useMutation({
    mutationFn: (enabled) => api.eqTendersConfig({ enabled }),
    onSuccess: refresh,
    onError,
  });

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="equities-tenders">
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="tenders-intro">
        A tender offer is a company's public offer to buy back its own shares at a stated price, or inside a stated range,
        for a limited time. An odd-lot provision means holders of fewer than 100 shares who tender all of them are bought in
        full, without the proration that larger holders face when more shares are offered than the company will take.
        This list is read by Claude from the SEC filing and must be checked against the filing itself before acting.
        Nothing here is a trade signal and nothing here places an order.
      </p>

      <Panel title="Open offers" icon="FileText" data-testid="tenders-list"
             actions={
               <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
                 <Switch label="Show expired" checked={showExpired} onChange={setShowExpired} data-testid="tenders-show-expired" />
                 {owner && (
                   <Switch label="Watcher" checked={q.data?.enabled !== false} onChange={(on) => toggle.mutate(on)}
                           disabled={toggle.isPending} data-testid="tenders-toggle" />
                 )}
                 {owner && (
                   <Button size="sm" variant="secondary" icon="RefreshCw" loading={poll.isPending}
                           onClick={() => poll.mutate()} data-testid="tenders-poll-now">
                     Poll now
                   </Button>
                 )}
               </div>
             }>
        <QueryBoundary query={q} loading={4} what="the tender offers" errorTestId="tenders-error"
                       isEmpty={() => false}>
          {(data) => {
            const offers = data?.offers || [];
            const open = orderOffers(offers.filter((o) => o.status !== "expired"));
            const expired = offers.filter((o) => o.status === "expired");
            const oddCount = open.filter(isOddLot).length;
            return (
              <div className="sn-stack">
                <StatusLine data={data} at={q.dataUpdatedAt} />
                {open.length === 0 ? (
                  <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="tenders-empty">
                    No open tender offers on file. New SC TO-I filings appear here after the next poll.
                  </p>
                ) : (
                  <>
                    <p className="sn-small sn-muted" style={{ margin: 0 }}>
                      {oddCount > 0
                        ? `${oddCount} offer${oddCount === 1 ? "" : "s"} with an odd-lot provision shown first and highlighted. `
                        : "No open offer has a clear odd-lot provision. "}
                      Gain for 99 shares is (offer price minus the latest close) times 99, less $2 of commissions; for a price
                      range it is figured at the low end. The market price is the last daily close, not a live quote.
                    </p>
                    <OffersTable rows={open} owner={owner} reread={reread} label="Open tender offers" testId="tenders-open-table" />
                  </>
                )}
                {showExpired && (
                  <div className="sn-stack" style={{ gap: "var(--space-1)" }} data-testid="tenders-expired">
                    <span className="sn-micro">Expired ({expired.length})</span>
                    {expired.length === 0
                      ? <span className="sn-small sn-muted">No expired offers on file.</span>
                      : <OffersTable rows={expired} owner={owner} reread={reread} label="Expired tender offers" testId="tenders-expired-table" />}
                  </div>
                )}
                <p className="sn-small sn-faint" style={{ margin: 0 }}>
                  Model {data?.model || "claude-sonnet-5"}, prompt version {data?.prompt_version || "n/a"}. Unread filings are retried
                  on later polls, at most three times. An alert is raised once per filing when the odd-lot provision is clear and the
                  gain for 99 shares is $50 or more.
                </p>
              </div>
            );
          }}
        </QueryBoundary>
      </Panel>
    </div>
  );
}
